import React from 'react';
import { Table } from 'react-bootstrap';
import { getDynamicColumns, parseDynamicLabel, getDimensionName, getColumnDatabaseName } from '../utils/analyticsUtils';
import SortableHeader from '../../../components/ui/SortableHeader';

const ResultsTable = ({ 
  results, 
  executedTimeFrame, 
  executedDimension, 
  expandedRows, 
  onToggleRowExpansion,
  sortColumn,
  sortDirection,
  onSort
}) => {
  if (executedTimeFrame > 0) {
    return (
      <>
        {Array.isArray(results) && results.map((timeFrameGroup, groupIndex) => (
          <div key={groupIndex} className="mb-4">
            <h5 className="text-primary mb-3">{timeFrameGroup.timeFrame}</h5>
            <TimeFrameTable
              results={timeFrameGroup.results}
              executedDimension={executedDimension}
              expandedRows={expandedRows}
              onToggleRowExpansion={onToggleRowExpansion}
              groupIndex={groupIndex}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={onSort}
            />
          </div>
        ))}
      </>
    );
  }

  return (
    <StandardTable
      results={results.items || results}
      executedDimension={executedDimension}
      expandedRows={expandedRows}
      onToggleRowExpansion={onToggleRowExpansion}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onSort={onSort}
    />
  );
};

const TimeFrameTable = ({ results, executedDimension, expandedRows, onToggleRowExpansion, groupIndex, sortColumn, sortDirection, onSort }) => {
  const dynamicColumns = getDynamicColumns(results);

  return (
    <Table striped bordered hover responsive>
      <thead>
        <tr>
          <TableHeaders 
            dynamicColumns={dynamicColumns} 
            executedDimension={executedDimension}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={onSort}
          />
        </tr>
      </thead>
      <tbody>
        {Array.isArray(results) && results.map((result, index) => {
          const parsedLabel = parseDynamicLabel(result.label);
          const rowKey = `tf-${groupIndex}-${index}`;
          
          return (
            <tr key={index}>
              <TableRow
                result={result}
                dynamicColumns={dynamicColumns}
                parsedLabel={parsedLabel}
                executedDimension={executedDimension}
                expandedRows={expandedRows}
                onToggleRowExpansion={onToggleRowExpansion}
                rowKey={rowKey}
              />
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

const StandardTable = ({ results, executedDimension, expandedRows, onToggleRowExpansion, sortColumn, sortDirection, onSort }) => {
  const dynamicColumns = getDynamicColumns(results);

  return (
    <Table striped bordered hover responsive>
      <thead>
        <tr>
          <TableHeaders 
            dynamicColumns={dynamicColumns} 
            executedDimension={executedDimension}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={onSort}
          />
        </tr>
      </thead>
      <tbody>
        {Array.isArray(results) && results.map((result, index) => {
          const parsedLabel = parseDynamicLabel(result.label);
          const rowKey = `main-${index}`;
          
          return (
            <tr key={index}>
              <TableRow
                result={result}
                dynamicColumns={dynamicColumns}
                parsedLabel={parsedLabel}
                executedDimension={executedDimension}
                expandedRows={expandedRows}
                onToggleRowExpansion={onToggleRowExpansion}
                rowKey={rowKey}
              />
            </tr>
          );
        })}
      </tbody>
    </Table>
  );
};

const TableHeaders = ({ dynamicColumns, executedDimension, sortColumn, sortDirection, onSort }) => {
  const getSortIcon = (column) => {
    const dbColumnName = getColumnDatabaseName(column, dynamicColumns, executedDimension);
    if (sortColumn !== dbColumnName) return '⇅';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  if (dynamicColumns.length > 0) {
    return (
      <>
        {dynamicColumns.map(column => (
          <SortableHeader 
            key={column} 
            column={column}
            onSort={onSort}
            getSortIcon={getSortIcon}
          >
            {column}
          </SortableHeader>
        ))}
        <SortableHeader 
          column="count"
          onSort={onSort}
          getSortIcon={getSortIcon}
          className="bg-primary text-white fw-bold"
        >
          Pedidos
        </SortableHeader>
        <SortableHeader 
          column="sum"
          onSort={onSort}
          getSortIcon={getSortIcon}
          className="bg-primary text-white fw-bold"
        >
          Suma
        </SortableHeader>
        <SortableHeader 
          column="average"
          onSort={onSort}
          getSortIcon={getSortIcon}
          className="bg-primary text-white fw-bold"
        >
          Promedio
        </SortableHeader>
        {executedDimension === 4 && (
          <>
            <SortableHeader 
              column="quantityCount"
              onSort={onSort}
              getSortIcon={getSortIcon}
              className="bg-success text-white fw-bold"
            >
              Productos Total
            </SortableHeader>
            <SortableHeader 
              column="quantityAverage"
              onSort={onSort}
              getSortIcon={getSortIcon}
              className="bg-success text-white fw-bold"
            >
              Productos Promedio
            </SortableHeader>
          </>
        )}
      </>
    );
  }

  return (
    <>
      <SortableHeader 
        column="label"
        onSort={onSort}
        getSortIcon={getSortIcon}
      >
        {getDimensionName(executedDimension)}
      </SortableHeader>
      <SortableHeader 
        column="count"
        onSort={onSort}
        getSortIcon={getSortIcon}
        className="bg-primary text-white fw-bold"
      >
        Pedidos
      </SortableHeader>
      <SortableHeader 
        column="sum"
        onSort={onSort}
        getSortIcon={getSortIcon}
        className="bg-primary text-white fw-bold"
      >
        Suma
      </SortableHeader>
      <SortableHeader 
        column="average"
        onSort={onSort}
        getSortIcon={getSortIcon}
        className="bg-primary text-white fw-bold"
      >
        Promedio
      </SortableHeader>
      {executedDimension === 4 && (
        <>
          <SortableHeader 
            column="quantityCount"
            onSort={onSort}
            getSortIcon={getSortIcon}
            className="bg-success text-white fw-bold"
          >
            Productos Total
          </SortableHeader>
          <SortableHeader 
            column="quantityAverage"
            onSort={onSort}
            getSortIcon={getSortIcon}
            className="bg-success text-white fw-bold"
          >
            Productos Promedio
          </SortableHeader>
        </>
      )}
    </>
  );
};

const TableRow = ({ 
  result, 
  dynamicColumns, 
  parsedLabel, 
  executedDimension, 
  expandedRows, 
  onToggleRowExpansion, 
  rowKey 
}) => {
  if (dynamicColumns.length > 0) {
    return (
      <>
        {dynamicColumns.map(column => (
          <td key={column}>{parsedLabel[column] || '-'}</td>
        ))}
        <OrdersCell
          result={result}
          expandedRows={expandedRows}
          onToggleRowExpansion={onToggleRowExpansion}
          rowKey={rowKey}
        />
        <td className="fw-bold text-primary">{result.sum != null ? result.sum.toLocaleString() : '0'}</td>
        <td className="fw-bold text-primary">{result.average != null ? result.average.toLocaleString() : '0'}</td>
        {executedDimension === 4 && (
          <>
            <td className="fw-bold text-success">{result.quantityCount != null ? result.quantityCount.toLocaleString() : '0'}</td>
            <td className="fw-bold text-success">{result.quantityAverage != null ? result.quantityAverage.toLocaleString() : '0'}</td>
          </>
        )}
      </>
    );
  }

  return (
    <>
      <td>{result.label || 'Sin etiqueta'}</td>
      <OrdersCell
        result={result}
        expandedRows={expandedRows}
        onToggleRowExpansion={onToggleRowExpansion}
        rowKey={rowKey}
      />
      <td className="fw-bold text-primary">{result.sum != null ? result.sum.toLocaleString() : '0'}</td>
      <td className="fw-bold text-primary">{result.average != null ? result.average.toLocaleString() : '0'}</td>
      {executedDimension === 4 && (
        <>
          <td className="fw-bold text-success">{result.quantityCount != null ? result.quantityCount.toLocaleString() : '0'}</td>
          <td className="fw-bold text-success">{result.quantityAverage != null ? result.quantityAverage.toLocaleString() : '0'}</td>
        </>
      )}
    </>
  );
};

const OrdersCell = ({ result, expandedRows, onToggleRowExpansion, rowKey }) => (
  <td className="fw-bold text-primary">
    <div className="d-flex align-items-center">
      <span className="me-2">{result.count != null ? result.count.toLocaleString() : '0'}</span>
      {result.orderIds && result.orderIds.length > 0 && (
        <button
          className="btn btn-link p-0 text-decoration-none"
          onClick={() => onToggleRowExpansion(rowKey)}
          style={{ fontSize: '0.7em', color: '#6c757d' }}
        >
          {expandedRows.has(rowKey) ? '▼' : '▶'}
        </button>
      )}
    </div>
    {expandedRows.has(rowKey) && result.orderIds && (
      <div className="mt-1">
        {result.orderIds.map(orderId => (
          <a
            key={orderId}
            href={`/orders/${orderId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="badge bg-secondary text-decoration-none me-1 mb-1"
            style={{ fontSize: '0.6em' }}
          >
            #{orderId}
          </a>
        ))}
      </div>
    )}
  </td>
);

export default ResultsTable;